import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getOnboardingConfig, calculateEduPro, createEduProApplication, verifyEduProApplication,
  ensureGuestId, getGuestId, clearGuestId, claimEduProApplication,
} from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { DocumentCapture } from '../components/DocumentCapture';
import { IdentityVerification } from '../components/IdentityVerification';
import { EduProContract } from '../components/EduProContract';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ArrowLeft, ArrowRight, Check, Globe, User, Users, ScanLine, FileText,
  ShieldCheck, CheckCircle, AlertCircle, Loader2, Sparkles, XCircle, FileSignature, UserPlus, Lock, RotateCcw, Copy
} from 'lucide-react';

const PROGRESS_KEY = 'edupro_progress';

const COUNTRIES = [
  { code: 'MX', name: 'México', flag: '🇲🇽', currency: 'MXN' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', currency: 'COP' },
];
const TERMS = [18, 24];

const STEPS = [
  { id: 1, name: 'País', icon: Globe },
  { id: 2, name: 'Solicitante', icon: User },
  { id: 3, name: 'Aval', icon: Users },
  { id: 4, name: 'Docs Solicitante', icon: ScanLine },
  { id: 5, name: 'Docs Aval', icon: FileText },
  { id: 6, name: 'Verificación', icon: ShieldCheck },
  { id: 7, name: 'Resultado', icon: CheckCircle },
];

const emptyPerson = () => ({
  full_name: '', national_id: '', tax_id: '', birth_date: '', phone: '', email: '',
  street: '', number: '', colony: '', postal_code: '', city: '', region: '', relationship: '',
});

const PersonForm = ({ data, setData, subject, isGuarantor, config }) => {
  if (!config) return null;
  const set = (f, v) => setData((prev) => ({ ...prev, [f]: v }));
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Nombre completo *</Label>
          <Input value={data.full_name} onChange={(e) => set('full_name', e.target.value)} className="mt-2" placeholder="Como aparece en tu identificación" data-testid={`${subject}-full_name`} />
        </div>
        <div>
          <Label>{config.national_id.label} *</Label>
          <Input value={data.national_id} onChange={(e) => set('national_id', e.target.value.toUpperCase())} maxLength={config.national_id.length} className="mt-2" placeholder={config.national_id.placeholder} data-testid={`${subject}-national_id`} />
          <p className="text-[11px] text-slate-400 mt-1">{config.national_id.help}</p>
        </div>
        <div>
          <Label>{config.tax_id.label} {config.tax_id.optional ? '' : '*'}</Label>
          <Input value={data.tax_id} onChange={(e) => set('tax_id', e.target.value.toUpperCase())} maxLength={config.tax_id.length} className="mt-2" placeholder={config.tax_id.placeholder} data-testid={`${subject}-tax_id`} />
        </div>
        <div>
          <Label>Fecha de nacimiento *</Label>
          <Input type="date" value={data.birth_date} onChange={(e) => set('birth_date', e.target.value)} className="mt-2" data-testid={`${subject}-birth_date`} />
        </div>
        <div>
          <Label>Teléfono *</Label>
          <div className="flex gap-2 mt-2">
            <span className="inline-flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500">{config.dial_code}</span>
            <Input value={data.phone} onChange={(e) => set('phone', e.target.value)} placeholder="10 dígitos" data-testid={`${subject}-phone`} />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Correo electrónico</Label>
          <Input type="email" value={data.email} onChange={(e) => set('email', e.target.value)} className="mt-2" placeholder="tucorreo@ejemplo.com" data-testid={`${subject}-email`} />
        </div>
        {isGuarantor && (
          <div className="md:col-span-2">
            <Label>Relación con el solicitante *</Label>
            <Select value={data.relationship} onValueChange={(v) => set('relationship', v)}>
              <SelectTrigger className="mt-2" data-testid={`${subject}-relationship`}><SelectValue placeholder="Selecciona..." /></SelectTrigger>
              <SelectContent>
                {['Padre / Madre', 'Cónyuge', 'Hermano/a', 'Familiar', 'Amigo/a', 'Otro'].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <h4 className="text-sm font-semibold text-slate-700 pt-2">Domicilio</h4>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Calle</Label><Input value={data.street} onChange={(e) => set('street', e.target.value)} className="mt-2" data-testid={`${subject}-street`} /></div>
        <div><Label>Número</Label><Input value={data.number} onChange={(e) => set('number', e.target.value)} className="mt-2" data-testid={`${subject}-number`} /></div>
        <div><Label>Colonia / Barrio</Label><Input value={data.colony} onChange={(e) => set('colony', e.target.value)} className="mt-2" data-testid={`${subject}-colony`} /></div>
        <div><Label>Código Postal</Label><Input value={data.postal_code} onChange={(e) => set('postal_code', e.target.value)} className="mt-2" data-testid={`${subject}-postal_code`} /></div>
        <div><Label>Ciudad *</Label><Input value={data.city} onChange={(e) => set('city', e.target.value)} className="mt-2" data-testid={`${subject}-city`} /></div>
        <div>
          <Label>{config.regions_label} *</Label>
          <Select value={data.region} onValueChange={(v) => set('region', v)}>
            <SelectTrigger className="mt-2" data-testid={`${subject}-region`}><SelectValue placeholder="Selecciona..." /></SelectTrigger>
            <SelectContent className="max-h-64">
              {config.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

const DocsGrid = ({ subject, config, country, applicationId, uploaded, onUploaded }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {config?.documents.map((doc) => (
      <DocumentCapture
        key={doc.key}
        doc={doc}
        subject={subject}
        country={country}
        applicationId={applicationId}
        value={uploaded[subject][doc.key]}
        onUploaded={onUploaded}
      />
    ))}
  </div>
);

const EduProApplicationWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, register } = useAuth();

  const [step, setStep] = useState(1);
  const [country, setCountry] = useState('MX');
  const [term, setTerm] = useState(24);
  const [config, setConfig] = useState(null);
  const [calc, setCalc] = useState(null);

  const [applicant, setApplicant] = useState(emptyPerson());
  const [guarantor, setGuarantor] = useState(emptyPerson());
  const [uploaded, setUploaded] = useState({ applicant: {}, guarantor: {} });

  const [applicationId, setApplicationId] = useState(null);
  const [consent, setConsent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showContract, setShowContract] = useState(false);
  const [contractSigned, setContractSigned] = useState(false);
  // Guest optional account creation
  const [acct, setAcct] = useState({ password: '', open: false });
  const [acctLoading, setAcctLoading] = useState(false);
  const [acctDone, setAcctDone] = useState(false);
  // Resume (guests)
  const [resumeData, setResumeData] = useState(null);
  const [showResume, setShowResume] = useState(false);
  const [copied, setCopied] = useState(false);

  // No login required: unauthenticated users get a guest session (no account as step 1).
  // Also honor ?r=<guest_id> resume links and offer to resume saved progress on this device.
  useEffect(() => {
    if (isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    const r = params.get('r');
    if (r) localStorage.setItem('edupro_guest_id', r.trim());
    ensureGuestId();
    try {
      const saved = JSON.parse(localStorage.getItem(PROGRESS_KEY) || 'null');
      if (saved && saved.step > 1 && saved.step < 7) { setResumeData(saved); setShowResume(true); }
    } catch (e) { /* ignore */ }
  }, [isAuthenticated, location.search]);

  // Persist progress on this device (guests)
  useEffect(() => {
    if (isAuthenticated || showResume) return;
    if (step >= 7) { localStorage.removeItem(PROGRESS_KEY); return; }
    localStorage.setItem(PROGRESS_KEY, JSON.stringify({ step, country, term, applicant, guarantor, uploaded, applicationId, updatedAt: Date.now() }));
  }, [step, country, term, applicant, guarantor, uploaded, applicationId, isAuthenticated, showResume]);

  const doResume = () => {
    const s = resumeData;
    if (s) {
      setCountry(s.country || 'MX'); setTerm(s.term || 24);
      setApplicant({ ...emptyPerson(), ...(s.applicant || {}) });
      setGuarantor({ ...emptyPerson(), ...(s.guarantor || {}) });
      setUploaded(s.uploaded || { applicant: {}, guarantor: {} });
      setApplicationId(s.applicationId || null);
      setStep(s.step || 1);
    }
    setShowResume(false);
  };
  const discardResume = () => {
    localStorage.removeItem(PROGRESS_KEY);
    setResumeData(null);
    setShowResume(false);
  };
  const copyResumeLink = () => {
    const link = `${window.location.origin}/edupro/apply?r=${getGuestId()}`;
    try { navigator.clipboard?.writeText(link)?.catch(() => {}); } catch (e) { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    getOnboardingConfig(country).then((r) => setConfig(r.data)).catch(() => {});
    calculateEduPro({ country, term_months: term, tier: 'good' }).then((r) => setCalc(r.data)).catch(() => {});
  }, [country, term]);

  const fmt = (v, cur) =>
    new Intl.NumberFormat(cur === 'COP' ? 'es-CO' : 'es-MX', {
      style: 'currency', currency: cur || 'MXN', minimumFractionDigits: 0,
    }).format(v);

  const onUploaded = (subject, key, result) => {
    setUploaded((prev) => ({ ...prev, [subject]: { ...prev[subject], [key]: result } }));
  };

  const requiredDocsDone = (subject) => {
    if (!config) return false;
    const required = config.documents.filter((d) => d.required).map((d) => d.key);
    return required.every((k) => uploaded[subject][k]);
  };

  const personValid = (p) => {
    const taxRequired = config && !config.tax_id.optional;
    return (
      p.full_name && p.national_id && p.birth_date && p.phone &&
      p.city && p.region && (!taxRequired || p.tax_id)
    );
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!country && !!term;
      case 2: return personValid(applicant);
      case 3: return personValid(guarantor) && !!guarantor.relationship;
      case 4: return requiredDocsDone('applicant');
      case 5: return requiredDocsDone('guarantor');
      default: return true;
    }
  };

  const buildPerson = (p) => ({
    full_name: p.full_name,
    national_id: p.national_id.toUpperCase(),
    tax_id: p.tax_id ? p.tax_id.toUpperCase() : '',
    birth_date: p.birth_date,
    phone: p.phone,
    email: p.email,
    relationship: p.relationship || undefined,
    address: {
      street: p.street, number: p.number, colony: p.colony,
      postal_code: p.postal_code, city: p.city, region: p.region,
    },
  });

  const ensureApplication = async () => {
    if (applicationId) return applicationId;
    setLoading(true);
    setError('');
    const build = (forceGuest) => ({
      country, term_months: term,
      applicant: buildPerson(applicant),
      guarantor: buildPerson(guarantor),
      guest_id: (!isAuthenticated || forceGuest) ? ensureGuestId() : undefined,
    });
    try {
      let res;
      try {
        res = await createEduProApplication(build(false));
      } catch (e1) {
        // Session missing/expired -> fall back to a guest session and retry once
        if (e1.response?.status === 401) {
          res = await createEduProApplication(build(true));
        } else {
          throw e1;
        }
      }
      setApplicationId(res.data.application_id);
      return res.data.application_id;
    } catch (e) {
      const status = e.response?.status;
      let detail = e.response?.data?.detail;
      if (typeof detail !== 'string') detail = null;
      setError(
        detail ||
        (status
          ? `No se pudo crear la solicitud (error ${status}). Por favor inténtalo de nuevo.`
          : 'No se pudo crear la solicitud. Revisa tu conexión e inténtalo de nuevo.')
      );
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setError('');
    if (step === 3) {
      try { await ensureApplication(); } catch { return; }
    }
    setStep((s) => Math.min(s + 1, 7));
  };
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const startVerification = async () => {
    setError('');
    setVerifying(true);
    try {
      const res = await verifyEduProApplication(applicationId);
      setVerifyResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error en la verificación');
      setVerifying(false);
    }
  };

  const approved = verifyResult?.status === 'approved';

  const handleCreateAccount = async () => {
    setError('');
    if (!applicant.email) { setError('Necesitamos tu correo para crear la cuenta. Vuelve al paso del solicitante para agregarlo.'); return; }
    if (!acct.password || acct.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setAcctLoading(true);
    try {
      const gid = getGuestId();
      await register({ email: applicant.email, password: acct.password, name: applicant.full_name, phone: applicant.phone });
      if (gid && applicationId) {
        try { await claimEduProApplication(applicationId, gid); } catch (e) { /* non-blocking */ }
      }
      clearGuestId();
      localStorage.removeItem(PROGRESS_KEY);
      setAcctDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear la cuenta (¿el correo ya está registrado?)');
    } finally {
      setAcctLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <EdFiLogo size="sm" linkTo="/" />
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="font-bold text-[#0A2463]">Ed.Fi</span>
            <span className="text-slate-300">×</span>
            <img src="/edupro-logo.png" alt="EduPro" className="h-5 w-auto" />
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200 py-5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 overflow-x-auto">
          <div className="flex items-center justify-between min-w-[560px]">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div key={s.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${done ? 'bg-emerald-500 text-white' : active ? 'bg-[#0A2463] text-white' : 'bg-slate-200 text-slate-400'}`}>
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] mt-1.5 ${active ? 'text-[#0A2463] font-semibold' : 'text-slate-400'}`}>{s.name}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-0.5 w-8 sm:w-12 mx-1 ${step > s.id ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2" data-testid="wizard-error">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}

        <Card>
          <CardContent className="pt-6">
            {/* STEP 1 */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-[#E91E8C]/10 rounded-full px-3 py-1 mb-3">
                    <Sparkles className="w-3.5 h-3.5 text-[#E91E8C]" />
                    <span className="text-xs font-semibold text-[#E91E8C]">Financiamiento EduPro · 1,800 USD</span>
                  </div>
                  <h2 className="text-2xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>¿Desde dónde solicitas?</h2>
                  <p className="text-slate-500 text-sm mt-1">Financiamos en tu moneda local con pagos flexibles.</p>
                </div>
                <div>
                  <Label>País de residencia</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {COUNTRIES.map((cn) => (
                      <button key={cn.code} onClick={() => setCountry(cn.code)} className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${country === cn.code ? 'border-[#0A2463] bg-[#0A2463]/5' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`wizard-country-${cn.code}`}>
                        <span className="text-3xl">{cn.flag}</span>
                        <div className="text-left"><p className="font-semibold text-slate-900">{cn.name}</p><p className="text-xs text-slate-400">Moneda: {cn.currency}</p></div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Plazo de pago</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {TERMS.map((t) => (
                      <button key={t} onClick={() => setTerm(t)} className={`p-4 rounded-xl border-2 transition-all ${term === t ? 'border-[#E91E8C] bg-[#E91E8C]/5' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`wizard-term-${t}`}>
                        <p className="font-bold text-slate-900">{t} meses</p>
                      </button>
                    ))}
                  </div>
                </div>
                {calc && (
                  <div className="bg-[#0A2463] rounded-2xl p-6 text-white flex flex-wrap items-center justify-between gap-4">
                    <div><p className="text-white/60 text-xs">Monto a financiar</p><p className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(calc.principal, calc.currency)}</p></div>
                    <div><p className="text-white/60 text-xs">Pago mensual (estimado)</p><p className="text-2xl font-black text-[#E91E8C]" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(calc.monthly_payment, calc.currency)}</p></div>
                    <div><p className="text-white/60 text-xs">Tasa</p><p className="text-lg font-bold">{calc.monthly_rate_percent}% mensual</p></div>
                  </div>
                )}
                {!isAuthenticated && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500 flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> Sin crear cuenta. Tu avance se guarda en este dispositivo.
                    </p>
                    <Button variant="ghost" size="sm" onClick={copyResumeLink} className="text-[#0A2463] text-xs" data-testid="copy-resume-link">
                      <Copy className="w-3.5 h-3.5 mr-1.5" /> {copied ? '¡Enlace copiado!' : 'Copiar enlace para retomar'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="space-y-5">
                <h2 className="text-2xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>Datos del solicitante</h2>
                <PersonForm data={applicant} setData={setApplicant} subject="applicant" isGuarantor={false} config={config} />
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>Datos del aval / co-deudor</h2>
                  <p className="text-slate-500 text-sm mt-1">Tu aval respalda el financiamiento y completará su propia verificación de identidad.</p>
                </div>
                <PersonForm data={guarantor} setData={setGuarantor} subject="guarantor" isGuarantor={true} config={config} />
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>Documentos del solicitante</h2>
                  <p className="text-slate-500 text-sm mt-1">Captura tu identificación y una selfie. Tus datos viajan cifrados.</p>
                </div>
                <DocsGrid subject="applicant" config={config} country={country} applicationId={applicationId} uploaded={uploaded} onUploaded={onUploaded} />
              </div>
            )}

            {/* STEP 5 */}
            {step === 5 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>Documentos del aval</h2>
                  <p className="text-slate-500 text-sm mt-1">Tu aval pasa exactamente la misma verificación que tú.</p>
                </div>
                <DocsGrid subject="guarantor" config={config} country={country} applicationId={applicationId} uploaded={uploaded} onUploaded={onUploaded} />
              </div>
            )}

            {/* STEP 6 */}
            {step === 6 && (
              <div className="space-y-6">
                {!verifyResult && !verifying && (
                  <div className="text-center py-6 space-y-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#0A2463] flex items-center justify-center mx-auto"><ShieldCheck className="w-8 h-8 text-white" /></div>
                    <div>
                      <h2 className="text-2xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>Verificación de identidad</h2>
                      <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">Validaremos identidad, biometría, registros oficiales y buró de crédito para ti y tu aval.</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-5 text-left max-w-md mx-auto space-y-2 text-sm text-slate-600">
                      {[`OCR y autenticidad de ${config?.id_document.label}`, 'Prueba de vida y biometría facial', `Registro oficial (${config?.registry_authority})`, `Buró de crédito (${config?.credit_bureau})`, 'Listas AML / PEP'].map((x, i) => (
                        <div key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> {x}</div>
                      ))}
                    </div>
                    <label className="flex items-start gap-3 max-w-md mx-auto text-left cursor-pointer">
                      <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="verify-consent" className="mt-0.5" />
                      <span className="text-xs text-slate-500">Autorizo a Ed.Fi a verificar mi identidad y consultar mi historial crediticio (Buró de Crédito / DataCrédito), y confirmo que mi aval otorga la misma autorización.</span>
                    </label>
                    <Button onClick={startVerification} disabled={!consent} className="bg-[#E91E8C] hover:bg-[#c21473] text-white px-8 py-6 text-base rounded-xl" data-testid="start-verification-btn">
                      Iniciar verificación <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
                {verifying && !verifyResult && (
                  <div className="text-center py-16">
                    <Loader2 className="w-10 h-10 text-[#0A2463] animate-spin mx-auto mb-4" />
                    <p className="text-slate-500 text-sm">Estableciendo conexión segura...</p>
                  </div>
                )}
                {verifyResult && (
                  <IdentityVerification result={verifyResult} onComplete={() => { setVerifying(false); setStep(7); }} />
                )}
              </div>
            )}

            {/* STEP 7 */}
            {step === 7 && verifyResult && (
              <div className="text-center py-6 space-y-6">
                {approved ? (
                  <>
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto"><CheckCircle className="w-10 h-10 text-emerald-600" /></div>
                    <div>
                      <h2 className="text-3xl font-black text-emerald-600" style={{ fontFamily: "'Outfit', sans-serif" }}>¡Aprobado!</h2>
                      <p className="text-slate-600 mt-1">Tu financiamiento EduPro está listo.</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-6 max-w-lg mx-auto grid grid-cols-2 gap-5 text-left">
                      <div><p className="text-xs text-slate-500">Monto aprobado</p><p className="text-2xl font-black text-emerald-600" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(verifyResult.amount_approved, verifyResult.currency)}</p></div>
                      <div><p className="text-xs text-slate-500">Pago mensual</p><p className="text-2xl font-black text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(verifyResult.monthly_payment, verifyResult.currency)}</p></div>
                      <div><p className="text-xs text-slate-500">Tasa mensual</p><p className="text-lg font-bold text-slate-900">{(verifyResult.interest_rate * 100).toFixed(1)}%</p></div>
                      <div><p className="text-xs text-slate-500">Plazo</p><p className="text-lg font-bold text-slate-900">{verifyResult.calc?.term_months} meses</p></div>
                    </div>
                    <p className="text-xs text-slate-400">Sin penalización por pago anticipado.</p>

                    {/* Contract signing */}
                    <div className="max-w-lg mx-auto">
                      {!contractSigned ? (
                        <Button onClick={() => setShowContract(true)} className="w-full bg-[#E91E8C] hover:bg-[#c21473] text-white px-8 py-6 text-base rounded-xl" data-testid="sign-contract-btn">
                          <FileSignature className="w-5 h-5 mr-2" /> Ver y firmar mi contrato
                        </Button>
                      ) : (
                        <div className="flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 rounded-xl py-3 font-semibold" data-testid="contract-signed-chip">
                          <CheckCircle className="w-5 h-5" /> Contrato firmado
                          <button onClick={() => setShowContract(true)} className="ml-2 text-sm underline text-emerald-700">Ver</button>
                        </div>
                      )}
                    </div>

                    {/* Optional account creation for guests */}
                    {!isAuthenticated && !acctDone && (
                      <div className="max-w-lg mx-auto text-left bg-slate-50 border border-slate-200 rounded-2xl p-5 mt-2" data-testid="guest-account-card">
                        <div className="flex items-center gap-2 mb-2">
                          <UserPlus className="w-5 h-5 text-[#0A2463]" />
                          <h3 className="font-bold text-[#0A2463]">Crea tu cuenta para dar seguimiento</h3>
                        </div>
                        <p className="text-xs text-slate-500 mb-4">Opcional. Guarda tu solicitud y consulta el estatus cuando quieras. Usaremos tu correo: <strong>{applicant.email || 'agrega tu correo en el paso 2'}</strong></p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="relative flex-1">
                            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <Input type="password" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} placeholder="Crea una contraseña" className="pl-9" data-testid="guest-password-input" />
                          </div>
                          <Button onClick={handleCreateAccount} disabled={acctLoading} className="bg-[#0A2463] hover:bg-[#08204f] text-white" data-testid="guest-create-account-btn">
                            {acctLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear cuenta'}
                          </Button>
                        </div>
                      </div>
                    )}
                    {acctDone && (
                      <div className="max-w-lg mx-auto bg-emerald-50 text-emerald-700 rounded-xl py-3 px-4 text-sm font-medium" data-testid="guest-account-done">
                        ¡Cuenta creada! Tu solicitud quedó guardada en tu dashboard.
                      </div>
                    )}

                    <Button
                      onClick={() => navigate((isAuthenticated || acctDone) ? '/dashboard' : '/')}
                      variant={(isAuthenticated || acctDone) ? 'default' : 'outline'}
                      className={`${(isAuthenticated || acctDone) ? 'bg-[#0A2463] hover:bg-[#08204f] text-white' : ''} px-8 py-6 text-base rounded-xl`}
                      data-testid="goto-dashboard-btn"
                    >
                      {(isAuthenticated || acctDone) ? <>Ir a mi Dashboard <ArrowRight className="w-4 h-4 ml-2" /></> : 'Volver al inicio'}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto"><XCircle className="w-10 h-10 text-amber-600" /></div>
                    <div>
                      <h2 className="text-3xl font-black text-amber-600" style={{ fontFamily: "'Outfit', sans-serif" }}>En revisión</h2>
                      <p className="text-slate-600 mt-1 max-w-md mx-auto">Necesitamos revisar manualmente tu verificación o la de tu aval. Un asesor te contactará muy pronto.</p>
                    </div>
                    <Button onClick={() => navigate('/dashboard')} variant="outline" className="px-8 py-6 rounded-xl" data-testid="goto-dashboard-btn">
                      Ir a mi Dashboard
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Nav buttons */}
        {step < 6 && (
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={handleBack} disabled={step === 1 || loading} data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
            </Button>
            <Button onClick={handleNext} disabled={!canProceed() || loading} className="bg-[#E91E8C] hover:bg-[#c21473] text-white" data-testid="wizard-next">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>

      {showContract && applicationId && (
        <EduProContract
          applicationId={applicationId}
          onClose={() => setShowContract(false)}
          onSigned={() => setContractSigned(true)}
        />
      )}

      {/* Resume saved progress (guests) */}
      <Dialog open={showResume} onOpenChange={(o) => { if (!o) setShowResume(false); }}>
        <DialogContent className="max-w-md" data-testid="resume-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0A2463]"><RotateCcw className="w-5 h-5 text-[#E91E8C]" /> Retoma tu solicitud</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Tienes una solicitud EduPro sin terminar en este dispositivo{resumeData?.updatedAt ? ` (guardada ${new Date(resumeData.updatedAt).toLocaleString('es-MX')})` : ''}. ¿Quieres continuar donde la dejaste?
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <Button onClick={doResume} className="flex-1 bg-[#E91E8C] hover:bg-[#c21473] text-white" data-testid="resume-continue-btn">
              Continuar solicitud <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={discardResume} variant="outline" className="flex-1" data-testid="resume-discard-btn">
              Empezar de nuevo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EduProApplicationWizard;
